import { api } from "./api/client.js";
import { withoutStaleClient } from "./clients.svelte.js";
import { withRetry } from "@bitcashier/shared/core/retry.js";
import { isReauthError } from "@bitcashier/shared/auth/reauth.js";
import { reportError } from "./observability/sentry.js";

// Port of src/user/context/PermissionsContext.tsx + the userPermissions redux slice.
//
// GET /ui/permissions returns { isAdmin, permissions: [{ clientId, scopes, roles }] }. Scopes and
// roles are per-client, so every check resolves against the CURRENTLY active client — a client
// switch re-reads the same payload rather than re-fetching (the React app re-fetched on switch;
// we keep that too, since the endpoint is scoped by the X-Client-Id header).

export const permissions = $state({
  loaded: false,
  isAdmin: false,
  entries: [],
  error: null,
});

export async function loadPermissions() {
  try {
    // Retried for the same reason the clients list is (see clients.svelte.js): this is a boot read,
    // and losing it to a blip is worse here for being silent — every scope check answers false, so
    // the user boots into a dashboard with the actions they own missing and nothing saying why.
    //
    // Wrapped in the same stale-client recovery for the same reason: this read boots in parallel
    // with the clients list carrying the same `active-client` cookie, so a cookie the gateway no
    // longer accepts rejects it too. The payload does not depend on the header — the API keys it on
    // the USER and returns an entry per client (UserPermissionsService.getPermissions(userId)) — so
    // the retry without it returns exactly what the first attempt was asking for.
    const data = await withoutStaleClient(() => withRetry(() => api.get("/ui/permissions")));
    permissions.isAdmin = data?.isAdmin ?? false;
    permissions.entries = data?.permissions ?? [];
    permissions.error = null;
  } catch (e) {
    // Match React: a failed permissions fetch leaves every scope check false rather than blocking
    // the app, so the user sees a dashboard with actions hidden instead of a dead screen.
    permissions.isAdmin = false;
    permissions.entries = [];
    permissions.error = e;
    // A dead session is not a boot failure. getToken() has already sent the user back through
    // Auth0 and the page is on its way out, so counting it here reported an ordinary re-login as
    // the dashboard failing to start — which is the one thing this report exists to measure.
    if (!isReauthError(e)) reportError(e, { boot: "permissions", status: e?.status ?? null });
  } finally {
    permissions.loaded = true;
  }
}

const forClient = (clientId) => permissions.entries.find((entry) => entry?.clientId === clientId);

export function checkScope(scope, clientId) {
  return forClient(clientId)?.scopes?.includes(scope) ?? false;
}

export function checkRole(role, clientId) {
  return forClient(clientId)?.roles?.includes(role) ?? false;
}
